import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { pbLevelIndex } from '@/data/mockSafeData';
import { usePersonalGrowth } from '@/hooks/usePersonalGrowth';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { formatScore } from '@/utils/formatScore';

// Se necesitan al menos dos puntos para trazar una línea; con uno solo el
// cálculo de la coordenada X dividiría entre cero.
const MIN_TREND_POINTS = 2;

/** Geometría del lienzo SVG por tamaño de pantalla. */
interface ChartGeometry {
  VW: number; VH: number;
  CL: number; CR: number; CT: number; CB: number;
  axisFontSize: number;
  monthFontSize: number;
  dotRadius: number;
  lastDotRadius: number;
  strokeWidth: number;
}

const DESKTOP_GEOMETRY: ChartGeometry = {
  VW: 560, VH: 210, CL: 80, CR: 545, CT: 14, CB: 175,
  axisFontSize: 10, monthFontSize: 10, dotRadius: 6, lastDotRadius: 9, strokeWidth: 3.5,
};

const MOBILE_GEOMETRY: ChartGeometry = {
  VW: 340, VH: 190, CL: 58, CR: 330, CT: 12, CB: 152,
  axisFontSize: 12, monthFontSize: 12, dotRadius: 5, lastDotRadius: 7.5, strokeWidth: 3,
};

export interface JourneyChartProps {
  /**
   * Se recibe del padre para compartir la misma clave de caché de TanStack Query.
   * Sin él, este componente dispara una segunda petición y en su primer render
   * no tiene datos todavía.
   */
  userId?: string;
}

export function JourneyChart({ userId }: JourneyChartProps) {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const lang = i18n.language.split('-')[0];
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    setIsDark(document.body.classList.contains('dark'));
    const obs = new MutationObserver(() => setIsDark(document.body.classList.contains('dark')));
    obs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const { data: growthData, isLoading: loadingGrowth } = usePersonalGrowth(userId);
  const trend = (growthData?.history ?? []).map(h => ({ month: h.month, avg: h.overallAverage }));

  // Sin historial suficiente no hay línea que dibujar. Antes de esta guarda el
  // componente indexaba pts[0] directamente y reventaba en el primer render,
  // cuando la consulta todavía no había resuelto.
  if (trend.length < MIN_TREND_POINTS) {
    return (
      <div className="ud-journey-chart-wrap ud-journey-empty">
        <i className="fas fa-chart-line ud-journey-empty-icon" aria-hidden="true" />
        <p className="ud-journey-empty-text">
          {loadingGrowth ? t('safe.journeyLoading') : t('safe.journeyEmpty')}
        </p>
      </div>
    );
  }

  const active = trend.filter(month => month.avg > 0);
  const first = active[0];
  const last = trend[trend.length - 1];
  const growth = first && last ? Math.max(0, last.avg - first.avg) : 0;
  const growthStr = `+${formatScore(growth)}`;
  const lastLvl = pbLevelIndex(last?.avg ?? 0);
  const levelLabelsJc = [t('safe.level0'), t('safe.level1'), t('safe.level2'), t('safe.level3')];
  const dirLabel = lastLvl < 3 ? t('safe.headingToLevel', { level: levelLabelsJc[lastLvl + 1] }) : t('safe.maxLevel');

  // En teléfono el SVG se escala al ancho del contenedor: con la geometría de
  // escritorio las etiquetas quedan ilegibles, así que se usa un lienzo más
  // estrecho y tipografías mayores en proporción.
  const geometry = isMobile ? MOBILE_GEOMETRY : DESKTOP_GEOMETRY;
  const { VW, VH, CL, CR, CT, CB, axisFontSize, monthFontSize } = geometry;
  const CW = CR - CL, CH = CB - CT;
  const n = trend.length;

  const xOf = (i: number) => CL + (CW / (n - 1)) * i;
  const yOf = (avg: number) => CB - ((avg - 1) / 3) * CH;

  const pts = trend.map((dp, i) => ({ x: xOf(i), y: yOf(dp.avg || 1) }));

  let linePath = `M ${pts[0].x},${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const cpx = (pts[i - 1].x + pts[i].x) / 2;
    linePath += ` C ${cpx},${pts[i - 1].y} ${cpx},${pts[i].y} ${pts[i].x},${pts[i].y}`;
  }
  const areaPath = `${linePath} L ${pts[n - 1].x},${CB} L ${pts[0].x},${CB} Z`;

  const guideColor = isDark ? 'rgba(255,255,255,0.18)' : '#E5E7EB';
  const labelColor = isDark ? '#94a3b8' : '#9CA3AF';
  const lastMonthColor = isDark ? '#a5b4fc' : '#342F82';

  const guides = [1, 2, 3, 4].map(lvl => {
    const y = yOf(lvl);
    return (
      <line key={lvl} x1={CL} y1={y} x2={CR} y2={y} stroke={guideColor} strokeWidth={1} strokeDasharray="4,3" />
    );
  });

  const lvlEntries: [string, number][] = [
    [t('safe.levelAbbr3'), 4], [t('safe.levelAbbr2'), 3], [t('safe.levelAbbr1'), 2], [t('safe.levelAbbr0'), 1],
  ];
  const yLabels = lvlEntries.map(([label, lvl]) => (
    <text
      key={lvl}
      x={CL - 5}
      y={yOf(lvl) + 4}
      textAnchor="end"
      fontSize={axisFontSize}
      fontFamily="Montserrat,sans-serif"
      fill={labelColor}
      fontWeight={600}
    >
      {label}
    </text>
  ));

  const dotColors = ['#4ADE80', '#34D399', '#38BDF8', '#818CF8', '#F472B6', '#FB923C'];
  const dotEls = trend.map((dp, i) => {
    const x = pts[i].x, y = pts[i].y;
    const color = dotColors[i] ?? dotColors[dotColors.length - 1];
    const isLast = i === n - 1;
    const mDate = new Date(`${dp.month}-01`);
    const mLabel = new Intl.DateTimeFormat(lang, { month: 'short' }).format(mDate);
    const pointBorder = isDark ? '#1e123e' : '#fff';

    return (
      <g key={dp.month}>
        {isLast ? (
          <>
            <circle cx={x} cy={y} r={geometry.lastDotRadius} fill={color} stroke={pointBorder} strokeWidth={3} />
            <rect x={x - 18} y={y - 32} width={36} height={17} rx={8} fill="#7C3AED" />
            <text x={x} y={y - 21} textAnchor="middle" fontSize={8} fontFamily="Montserrat,sans-serif" fill="#fff" fontWeight={800}>{t('safe.todayLabel').toUpperCase()}</text>
          </>
        ) : (
          <circle cx={x} cy={y} r={dp.avg > 0 ? geometry.dotRadius : 3} fill={dp.avg > 0 ? color : guideColor} stroke={pointBorder} strokeWidth={2.5} />
        )}
        <text x={x} y={VH - 6} textAnchor="middle" fontSize={monthFontSize} fontFamily="Montserrat,sans-serif" fill={isLast ? lastMonthColor : labelColor} fontWeight={isLast ? 700 : 400}>{mLabel}</text>
      </g>
    );
  });

  return (
    <div className="ud-journey-chart-wrap">
      <svg
        viewBox={`0 0 ${VW} ${VH}`}
        className="ud-journey-svg"
        role="img"
        aria-label={t('safe.journeyChartLabel')}
      >
        <defs>
          <linearGradient id="jAreaFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={isDark ? '#818CF8' : '#342F82'} stopOpacity={isDark ? 0.2 : 0.13} />
            <stop offset="100%" stopColor="#342F82" stopOpacity={0.01} />
          </linearGradient>
          <linearGradient id="jLineGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="#4ADE80" />
            <stop offset="40%"  stopColor="#38BDF8" />
            <stop offset="75%"  stopColor="#818CF8" />
            <stop offset="100%" stopColor="#F472B6" />
          </linearGradient>
        </defs>
        {guides}
        {yLabels}
        <path d={areaPath} fill="url(#jAreaFill)" />
        <path d={linePath} fill="none" stroke="url(#jLineGrad)" strokeWidth={geometry.strokeWidth} strokeLinecap="round" />
        {dotEls}
      </svg>
      <div className="ud-jt-stats">
        <div className="ud-jt-stat">
          <i className="fas fa-chart-line ud-jt-stat-icon ud-jt-stat-icon--growth" aria-hidden="true" />
          <div>
            <div className="ud-jt-stat-val">{growthStr}</div>
            <div className="ud-jt-stat-key">{t('safe.pointsInMonths', { n: active.length }).toUpperCase()}</div>
          </div>
        </div>
        <div className="ud-jt-stat">
          <i className="fas fa-fire ud-jt-stat-icon ud-jt-stat-icon--streak" aria-hidden="true" />
          <div>
            <div className="ud-jt-stat-val">{active.length}/{trend.length}</div>
            <div className="ud-jt-stat-key">{t('safe.monthsWithFeedback').toUpperCase()}</div>
          </div>
        </div>
        <div className="ud-jt-stat">
          <i className="fas fa-rocket ud-jt-stat-icon ud-jt-stat-icon--direction" aria-hidden="true" />
          <div>
            <div className="ud-jt-stat-val">↑</div>
            <div className="ud-jt-stat-key">{dirLabel.toUpperCase()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
